<template>
  <v-card class="contact-info">

    <div class="loader-wrapper"
         v-if="!businessTerm || loading">
      <VcLoader class="circular-loader"/>
    </div>

    <ExpansionPanels v-if="businessTerm && !loading"
                     :items="items"
                     :active-panels="activePanels"
                     :direction="direction"
                     @panel-change="onPanelChange"
                     :on-hover-color="hoverColor"
                     :disable-hover="isMobile">

      <template slot="header"
                slot-scope="headerSlot"
                v-if="headerSlot.item.contentCmp === 'MattersList'">
        <div class="header-cmp">

          <v-tooltip top
                     content-class="edit-tooltip"
                     :disabled="isMobile">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="add-matter-button"
                      @click="addMatterClick"
                      v-bind="attrs"
                      v-on="on">
                icon-Plus
              </v-icon>
            </template>
            <span>{{$t('client.matters.matter_page.add_matter')}}</span>
          </v-tooltip>

        </div>
      </template>

      <template slot="content"
                slot-scope="contentSlot">
        <component :is="contentSlot.item.contentCmp"
                   :contact="contact"
                   :matters="matters"
                   :direction="direction"/>
      </template>

    </ExpansionPanels>

  </v-card>
</template>

<script>
  import {ContactDetails} from '@/modules/matters/components';
  import {ExpansionPanels} from "@/modules/common/components";
  import MattersList from './MattersList';
  import {storageService, contactService} from "@/modules/common/services";
  import {mapGetters} from "vuex";

  export default {
    name: "ContactInfo",
    components: { ExpansionPanels, ContactDetails, MattersList },
    props: {
      contact: {
        type: Object,
        required: true
      },
      direction: {
        type: String,
        default: 'ltr'
      }
    },
    data() {
      return {
        matters: [],
        loading: true
      }
    },
    async created() {
      this.$frontageApi.listen('fetch-matter', this.onFetchMatter);
      await this.getMatters();
      this.loading = false;
    },
    computed: {
      ...mapGetters("brandModule", ["hoverColor"]),
      ...mapGetters(['isMobile']),
      ...mapGetters("accountModule", ["businessTerm"]),
      ...mapGetters('mattersModule', ['matter', 'currContact']),
      activePanels() {
        return storageService.get('contactActivePanels') || ['ContactDetails', 'MattersList'];
      },
      items() {
        let panels = [{name: this.$t('client.matters.matter_page.contact.primary_contact'), contentCmp: 'ContactDetails', id: 'ContactDetails', trackingLabel: 'contact panel'}];
        panels.push({name: `${this.$t(this.businessTerm.capitalized_plural)} (${this.matters.length})`, contentCmp: 'MattersList', id: 'MattersList', trackingLabel: 'matter panel'});
        return panels;
      }
    },
    methods: {
      onPanelChange(newVal, oldVal) {
        storageService.save('contactActivePanels', newVal.map(panel => { return this.items[panel].contentCmp }));
        const collapsed = oldVal.filter(panel => !newVal.includes(panel));
        const expanded = newVal.filter(panel => !oldVal.includes(panel));

        for (let i = 0; i < collapsed.length; i++) {
          this.$track(`Collapse ${this.items[collapsed[i]].trackingLabel}`);
        }

        for (let i = 0; i < expanded.length; i++) {
          this.$track(`Expand ${this.items[expanded[i]].trackingLabel}`);
        }
      },
      async getMatters() {
        // todo: get it from store so this component can be stupid and not make action
        const res = await contactService.getMattersForContact(this.contact.uid);

        if (res.data.error) {
          console.error(res.data.error);
          return;
        }

        let currentMatter;
        if (this.matter){
          currentMatter = res.data.matters.find(matter => matter.uid === this.matter.uid);
        }

        if (currentMatter) {
          currentMatter.current_matter = true;
        }

        this.matters = res.data.matters
          .filter(matter => !matter.deleted)
          .sort((a,b) => (a.display_name.toLowerCase() > b.display_name.toLowerCase()) ? 1 : ((b.display_name.toLowerCase() > a.display_name.toLowerCase()) ? -1 : 0));
      },
      addMatterClick() {
        this.$frontageApi.send('matter_action', {action: 'add_matter', uid: this.contact.uid});
        this.$track('Add matter from client card');
      },
      onFetchMatter({clientUid}) {
        if (this.currContact.uid !== clientUid) return;
        this.getMatters();
      }
    },
    watch: {
      currContact() {
        this.getMatters()
      },
      matter() {
        this.matters.forEach(matter => matter.current_matter = matter.uid === this.matter.uid);
      }
    },
    destroyed() {
      this.$frontageApi.remove('fetch-matter', this.onFetchMatter);
    }
  }
</script>

<style scoped lang="scss">
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_mixins.scss";

  .v-card {
    box-shadow: none;
  }

  .contact-info {
    width: 100%;
    height: 100%;
    overflow: scroll;
    @include hide-scrollbar;

    .loader-wrapper {
      height: 100%;
      text-align: center;

      .circular-loader {
        top: 50%;
        transform: translateY(-50%);
      }
    }

    .add-matter-button {
      font-size: 18px;
      @include i-position-mirror(right, 18px);
      top: 18px;
      height: 30px;
      width: 30px;
      position: absolute;
      color: $grey-color-5;
      border-radius: 50%;
      transition: color 0.2s, background-color 0.2s;

      .matter-page-wrapper &, .matter-pane-desktop & {

        &:hover {
          color: $navy-color-1;
          background-color: $grey-color-10;
        }
      }
    }
  }

  .edit-tooltip {
    font-family: var(--primary-font-family);
    font-size: 11px;
    line-height: 18px;
    margin-top: 5px;
    padding: 2px 10px;
    z-index: 50 !important;
  }
</style>
